import {
	AfterViewInit,
	Component,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Subscription } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import {
	AlarmStatus,
	alarmOverrideText,
	armedAlarmText,
	disarmedAlarmText,
} from 'src/app/models/data/ajax';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { EventHistory } from 'src/app/models/model';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { UserProfileService } from 'src/app/services/user-profile.service';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../../components/data-set/data-set.component';
import { DataSetSearchComponent } from '../../../../components/data-set/data-set-search.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import {
	DataSetTableComponent,
	DataSetTableRowSelectionComponent,
} from '../../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
	EventTypeFormatPipe,
	EndVisitTypeFormatPipe,
	AccessDateTimePipe,
} from '../../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../../components/data-set/data-set-loading-row.component';

@Component({
	selector: 'app-property-event-history',
	templateUrl: './property-event-history.component.html',
	styleUrls: [
		'./property-event-history.component.scss',
		'../../../common/main.scss',
	],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCellDef,
		MatCell,
		DataSetTableRowSelectionComponent,
		MatSortHeader,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		EventTypeFormatPipe,
		EndVisitTypeFormatPipe,
		AccessDateTimePipe,
		DataSetLoadingRowComponent,
	],
})
export class PropertyEventHistoryComponent
	implements OnInit, AfterViewInit, OnDestroy
{
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;

	AlarmStatus = AlarmStatus;
	displayedColumns: string[] = [
		'rowSelection',
		'userName',
		'companyName',
		'deviceLocation',
		'serialNumber',
		'eventType',
		'eventStart',
		'eventEnd',
		'endVisitType',
		'rowMenu',
	];
	dataSource = new MatTableDataSource<EventHistory>();

	@Input() propertyId = '';
	@Input() viewingCompanyId?: string;

	private filterSubscription?: Subscription;
	canGenerateEventHistoryReport!: boolean;
	isDownloadReportDisabled = false;
	filters: QueryParam[] = [];

	filterLabels = {
		defaultPlaceholder: $localize`:@@propertyEventHistory-defaultPlaceholder:Search serial number...`,
		userName: $localize`:@@propertyEventHistory-filterLabelUserName:User Name`,
		companyName: $localize`:@@propertyEventHistory-filterLabelCompanyName:Company Name`,
		deviceLocation: $localize`:@@propertyEventHistory-filterLabelDeviceLocation:Device Location`,
		serialNumber: $localize`:@@propertyEventHistory-filterLabelSerialNumber:Serial Number`,
		eventType: $localize`:@@propertyEventHistory-filterLabelEventType:Event Type`,
	};

	getPropertyEventHistoryLoadingKey = ApiLoadingKeys.getPropertyEventHistory;
	getEventHistoryReportLoadingKey = ApiLoadingKeys.getEventHistoryReport;
	finishedLoadingInitData: boolean = false;

	disarmedAlarmText = disarmedAlarmText;
	armedAlarmText = armedAlarmText;
	alarmOverrideText = alarmOverrideText;

	constructor(
		private apiService: ApiService,
		private readonly systemCapabilityService: SystemCapabilityService,
		public readonly loadingService: LoadingIndicatorService,
		private readonly userProfileService: UserProfileService
	) {}

	async ngOnInit(): Promise<void> {
		await this.updateEventHistoryAccess();
	}

	ngAfterViewInit(): void {
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				tap((filters) => (this.filters = filters)),
				mergeMap(async (filter) => {
					const result = await this.apiService.getPropertyEventHistory(
						this.propertyId,
						filter
					);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((history) => {
				this.dataSource.data = history;
			});
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	async generateEventHistoryReport(): Promise<void> {
		this.isDownloadReportDisabled = true;
		await this.apiService
			.getEventHistoryReport(this.propertyId, 'properties', this.filters)
			.then((_) => {
				this.isDownloadReportDisabled = false;
			});
	}

	getSystemCapabilityService(): SystemCapabilityService {
		return this.systemCapabilityService;
	}

	getApiService(): ApiService {
		return this.apiService;
	}

	formatSelectedUserName = (item: EventHistory): string => {
		const firstName = item.entityRelationship?.childUser?.firstName || '';
		const lastName = item.entityRelationship?.childUser?.lastName || '';
		const lastNameInitial = lastName.charAt(0).toUpperCase();

		if (firstName && lastNameInitial) {
			return `${firstName} ${lastNameInitial}`;
		} else if (firstName) {
			return firstName;
		} else if (lastName) {
			return lastName;
		}

		return (
			item.entityRelationship?.childUser?.email ||
			$localize`:@@selectedDisplayFormat-unknownUser:Unknown User`
		);
	};

	rowId(row: EventHistory): string {
		return (
			`${row.deviceId}-${row.utcStartDate}-${row.entityRelationshipId}` ||
			'unknown-id'
		);
	}

	async updateEventHistoryAccess(): Promise<void> {
		// Only SentriKey Access (Sentrilock) users can access event history
		const userProfile = await this.userProfileService.getUserProfile();
		const isSentriKeyAccessUser =
			userProfile.activeRoleDetails?.company?.isSentrilock ?? false;

		this.canGenerateEventHistoryReport =
			isSentriKeyAccessUser &&
			((this.systemCapabilityService.has(
				SystemCapabilityEnum.propertyManagement
			) &&
				this.systemCapabilityService.has(
					SystemCapabilityEnum.globalCompanyManagement
				)) ||
				this.systemCapabilityService.has(SystemCapabilityEnum.runReports));
	}
}
